import { foodMatchesSearch, normalizeAppState } from "./domain";
import type { AppState, FoodDefinition } from "./types";

export const LEGACY_STATE_KEY = "tren.v1";
export const MIGRATION_BACKUP_KEY = "tren.v1.migration-backup";
const DB_NAME = "tren-cut";
const DB_VERSION = 2;

export type StateStore = {
  state: AppState;
  durable: boolean;
  migrated: boolean;
  save: (state: AppState) => Promise<void>;
  clear: () => Promise<void>;
};

let dbPromise: Promise<IDBDatabase> | null = null;

export function openTrenDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains("state")) db.createObjectStore("state");
      if (!db.objectStoreNames.contains("meta")) db.createObjectStore("meta");
      if (!db.objectStoreNames.contains("foods")) {
        const foods = db.createObjectStore("foods", { keyPath: "storageId" });
        foods.createIndex("tokens", "tokens", { multiEntry: true });
        foods.createIndex("catalogVersion", "catalogVersion");
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB could not be opened."));
  });
  return dbPromise;
}

export async function createStateStore(): Promise<StateStore> {
  let legacy: unknown = {};
  let legacyRaw: string | null = null;
  try {
    legacyRaw = localStorage.getItem(LEGACY_STATE_KEY);
    if (legacyRaw) legacy = JSON.parse(legacyRaw);
  } catch {
    legacy = {};
  }

  try {
    const db = await openTrenDb();
    const existing = await idbGet<AppState>(db, "state", "current");
    const migrated = !existing;
    const legacyState = normalizeAppState(legacy);
    const existingState = existing ? normalizeAppState(existing) : undefined;
    // localStorage is retained as a synchronous write-ahead journal. If a page
    // closes while an IndexedDB transaction is still settling, recover that
    // last acknowledged edit and immediately copy it back into IndexedDB.
    const recoverFromJournal = Boolean(existingState && legacyRaw && legacyState.updatedAt);
    const state = recoverFromJournal ? legacyState : (existingState ?? legacyState);
    if (migrated) {
      if (legacyRaw && !localStorage.getItem(MIGRATION_BACKUP_KEY)) {
        localStorage.setItem(MIGRATION_BACKUP_KEY, legacyRaw);
      }
      await writeState(db, state);
      await idbPut(db, "meta", { migratedAt: new Date().toISOString(), from: LEGACY_STATE_KEY }, "migration");
    } else if (recoverFromJournal) {
      await writeState(db, state);
    }
    let writeQueue = Promise.resolve();
    return {
      state,
      durable: true,
      migrated,
      save: async (next) => {
        const raw = JSON.stringify({ ...next, updatedAt: new Date().toISOString() });
        const normalized = normalizeAppState(JSON.parse(raw));
        localStorage.setItem(LEGACY_STATE_KEY, JSON.stringify(normalized));
        writeQueue = writeQueue.then(() => writeState(db, normalized));
        await writeQueue;
      },
      clear: async () => {
        await idbDelete(db, "state", "current");
        localStorage.removeItem(LEGACY_STATE_KEY);
        localStorage.removeItem(MIGRATION_BACKUP_KEY);
      }
    };
  } catch {
    const state = normalizeAppState(legacy);
    return {
      state,
      durable: false,
      migrated: false,
      save: async (next) => localStorage.setItem(LEGACY_STATE_KEY, JSON.stringify(next)),
      clear: async () => {
        localStorage.removeItem(LEGACY_STATE_KEY);
        localStorage.removeItem(MIGRATION_BACKUP_KEY);
      }
    };
  }
}

export async function putFoodBatch(version: string, foods: FoodDefinition[]): Promise<void> {
  const db = await openTrenDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction("foods", "readwrite");
    const store = tx.objectStore("foods");
    foods.forEach((food) => store.put({ ...food, storageId: `${version}|${food.id}` }));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("Food catalog write failed."));
    tx.onabort = () => reject(tx.error ?? new Error("Food catalog write was aborted."));
  });
}

export async function searchStoredFoods(version: string, query: string, limit = 40): Promise<FoodDefinition[]> {
  const db = await openTrenDb();
  const firstToken = query.split(/\s+/).find(Boolean);
  if (!firstToken) return [];
  return new Promise((resolve, reject) => {
    const tx = db.transaction("foods", "readonly");
    const index = tx.objectStore("foods").index("tokens");
    const range = IDBKeyRange.bound(firstToken, `${firstToken}\uffff`);
    const request = index.openCursor(range);
    const rows: FoodDefinition[] = [];
    const seen = new Set<string>();
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor || rows.length >= limit) return resolve(rows);
      const value = cursor.value as FoodDefinition & { storageId: string };
      if (value.catalogVersion === version && foodMatchesSearch(value, query) && !seen.has(value.id)) {
        const { storageId: _storageId, ...food } = value;
        rows.push(food);
        seen.add(food.id);
      }
      cursor.continue();
    };
    request.onerror = () => reject(request.error ?? new Error("Food search failed."));
  });
}

export async function getCatalogMeta<T>(key: string): Promise<T | undefined> {
  const db = await openTrenDb();
  return idbGet<T>(db, "meta", key);
}

export async function setCatalogMeta<T>(key: string, value: T): Promise<void> {
  const db = await openTrenDb();
  await idbPut(db, "meta", value, key);
}

export async function deleteCatalogVersion(version: string): Promise<void> {
  const db = await openTrenDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction("foods", "readwrite");
    const index = tx.objectStore("foods").index("catalogVersion");
    const request = index.openKeyCursor(IDBKeyRange.only(version));
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) return;
      tx.objectStore("foods").delete(cursor.primaryKey);
      cursor.continue();
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("Old catalog cleanup failed."));
  });
}

async function writeState(db: IDBDatabase, state: AppState): Promise<void> {
  await idbPut(db, "state", state, "current");
}

function idbGet<T>(db: IDBDatabase, storeName: string, key: IDBValidKey): Promise<T | undefined> {
  return new Promise((resolve, reject) => {
    const request = db.transaction(storeName, "readonly").objectStore(storeName).get(key);
    request.onsuccess = () => resolve(request.result as T | undefined);
    request.onerror = () => reject(request.error);
  });
}

function idbPut(db: IDBDatabase, storeName: string, value: unknown, key?: IDBValidKey): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    const store = tx.objectStore(storeName);
    key === undefined ? store.put(value) : store.put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function idbDelete(db: IDBDatabase, storeName: string, key: IDBValidKey): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    tx.objectStore(storeName).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
